'use client';

import type { Column } from '@tanstack/react-table';
import type { ChangeEvent, ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ExtendedColumnFilter } from '@/types/data-table';

type RangeColumnMeta = {
  range?: [number, number];
  label?: string;
};

interface DataTableRangeFilterProps<TData> extends ComponentProps<'div'> {
  filter: ExtendedColumnFilter<TData>;
  column: Column<TData>;
  inputId: string;
  onFilterUpdate: (
    filterId: string,
    updates: Partial<Omit<ExtendedColumnFilter<TData>, 'filterId'>>,
  ) => void;
}

type RangeValidationInput = {
  value: string;
  isMin?: boolean;
  currentValue: ExtendedColumnFilter<unknown>['value'];
  min: number;
  max: number;
};

function parseNumericValue(value: string): number | null {
  if (value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function getCurrentValues(
  currentValue: ExtendedColumnFilter<unknown>['value'],
): [string, string] {
  if (Array.isArray(currentValue)) {
    return [currentValue[0] ?? '', currentValue[1] ?? ''];
  }
  return ['', ''];
}

function calculateBounds(
  otherValue: string,
  isMin: boolean | undefined,
  min: number,
  max: number,
): { lower: number; upper: number } {
  const numericOther = parseNumericValue(otherValue);
  const hasOtherNumber = numericOther !== null;

  return {
    lower: hasOtherNumber && !isMin ? numericOther : min,
    upper: hasOtherNumber && isMin ? numericOther : max,
  };
}

type BoundsCheckParams = {
  numericValue: number | null;
  isMin: boolean | undefined;
  bounds: { lower: number; upper: number };
  min: number;
  max: number;
};

function isValueWithinBounds({
  numericValue,
  isMin,
  bounds,
  min,
  max,
}: BoundsCheckParams): boolean {
  if (numericValue === null) {
    return true;
  }

  const lowerBound = isMin ? min : bounds.lower;
  const upperBound = isMin ? bounds.upper : max;

  return numericValue >= lowerBound && numericValue <= upperBound;
}

function getValidatedRangeValue({
  value,
  isMin,
  currentValue,
  min,
  max,
}: RangeValidationInput): [string, string] | null {
  const numericValue = parseNumericValue(value);
  if (numericValue === null && value !== '') {
    return null;
  }

  const currentValues = getCurrentValues(currentValue);
  const otherValue = isMin
    ? (currentValues[1] ?? '')
    : (currentValues[0] ?? '');

  const bounds = calculateBounds(otherValue, isMin, min, max);

  if (
    !isValueWithinBounds({
      numericValue,
      isMin,
      bounds,
      min,
      max,
    })
  ) {
    return null;
  }

  return isMin ? [value, otherValue] : [otherValue, value];
}

export function DataTableRangeFilter<TData>({
  filter,
  column,
  inputId,
  onFilterUpdate,
  className,
  ...props
}: DataTableRangeFilterProps<TData>) {
  const meta = column.columnDef.meta as RangeColumnMeta | undefined;

  const [min, max] = useMemo(() => {
    const range = meta?.range;
    if (range) {
      return range;
    }

    const values = column.getFacetedMinMaxValues();
    if (!values) {
      return [0, 100];
    }

    return [values[0], values[1]];
  }, [column, meta?.range]);

  const formatValue = useCallback((value: string | number | undefined) => {
    if (value === undefined || value === '') {
      return '';
    }
    const numValue = Number(value);
    return Number.isNaN(numValue)
      ? ''
      : numValue.toLocaleString(undefined, {
          maximumFractionDigits: 0,
        });
  }, []);

  const value = useMemo(() => {
    if (Array.isArray(filter.value)) {
      return filter.value.map(formatValue);
    }
    return [formatValue(filter.value), ''];
  }, [filter.value, formatValue]);

  const onRangeValueChange = useCallback(
    (value: string, isMin?: boolean) => {
      const nextValues = getValidatedRangeValue({
        value,
        isMin,
        currentValue: filter.value,
        min,
        max,
      });

      if (!nextValues) {
        return;
      }

      onFilterUpdate(filter.filterId, { value: nextValues });
    },
    [filter.filterId, filter.value, min, max, onFilterUpdate],
  );

  return (
    <div
      className={cn('flex w-full items-center gap-2', className)}
      data-slot='range'
      {...props}
    >
      <Input
        aria-label={`${meta?.label} minimum value`}
        aria-valuemax={max}
        aria-valuemin={min}
        className='h-8 w-full rounded'
        data-slot='range-min'
        defaultValue={value[0]}
        id={`${inputId}-min`}
        inputMode='numeric'
        max={max}
        min={min}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onRangeValueChange(event.target.value, true)
        }
        placeholder={min.toString()}
        type='number'
      />
      <span className='sr-only shrink-0 text-muted-foreground'>to</span>
      <Input
        aria-label={`${meta?.label} maximum value`}
        aria-valuemax={max}
        aria-valuemin={min}
        className='h-8 w-full rounded'
        data-slot='range-max'
        defaultValue={value[1]}
        id={`${inputId}-max`}
        inputMode='numeric'
        max={max}
        min={min}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onRangeValueChange(event.target.value)
        }
        placeholder={max.toString()}
        type='number'
      />
    </div>
  );
}
